import { useState } from 'react'
import {
  RiCalendarScheduleLine,
  RiDeleteBinLine,
  RiLiveLine,
  RiLock2Line,
  RiLockUnlockFill,
  RiMore2Fill,
  RiPencilLine,
  RiUserForbidLine
} from '@remixicon/react'
import i18n from 'src/i18n'
import { BlockCommentsModal } from 'src/pages/CommunityDetails/Posts/blockCommentsModal'
import BanMemberModal from 'src/pages/CommunityDetails/Posts/BanMemberModal'
import { EditPost } from 'src/pages/CommunityDetails/Posts/editPost'
import { usePublishSchedulePost } from 'src/hook/usePublishSchedulePost'
import { Action, ComponentName, Entity, System } from 'src/utils/datahubDefaultEvent'
import { CommunityTypeConsume } from 'src/pages/communityHome'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import { useSendDatahubDefaultEvents } from 'src/hook/useSendDatahubDefaultEvents'
import { useDeviceInfo } from '@hotmart-org-ca/space-lib/dist/hooks'
import { PostStatus } from 'src/hook/usePosts'
import { RemovePost } from 'src/components/Modal/RemovePost'
import { ActionButton } from '../feedCardHeaderActions/feedCardHeaderActions.styles'
import { FeedCardHeaderDropdownItem } from '../feedCardHeaderDropdownItem'
import { Dropdown } from './feedCardHeaderDropdown.styles'
import { FeedCardHeaderDropdownProps } from './feedCardHeaderDropdown.types'

const FeedCardHeaderDropdown = ({
  post,
  canBlockComments,
  canBanUser,
  canDeletePost,
  canEditPost,
  canViewLiveReport,
  canPublishScheduledPost
}: FeedCardHeaderDropdownProps) => {
  const { dispatchDatahubDefaultEvents } = useSendDatahubDefaultEvents()
  const [openDropdown, setOpenDropdown] = useState(false)
  const [openBlockCommentsModal, setOpenBlockCommentsModal] = useState(false)
  const [openModalBanMember, setOpenModalBanMember] = useState(false)
  const [openModalEditPost, setOpenModalEditPost] = useState(false)
  const [openModalDeletePost, setOpenModalDeletePost] = useState(false)
  const { community } = useCommunityDetails()
  const deviceInfo = useDeviceInfo()
  const { onPublish } = usePublishSchedulePost({ postId: post.id })

  const isScheduledPost = post?.postStatus === PostStatus.SCHEDULED

  const handleOpenDropdown = () => {
    setOpenDropdown(true)
  }

  const handleCloseDropdown = () => {
    setOpenDropdown(false)
  }

  const handleBlockComments = () => {
    handleCloseDropdown()
    setOpenBlockCommentsModal(true)
  }

  const handleCloseModalBlockComments = () => {
    setOpenBlockCommentsModal(false)
  }

  const handleBanUser = () => {
    handleCloseDropdown()
    setOpenModalBanMember(true)
  }

  const handleCloseModalBanMember = () => {
    setOpenModalBanMember(false)
  }

  const handleScheduledPostPublish = () => {
    handleCloseDropdown()
    onPublish()
  }

  const handleOpenLiveReport = () => {
    handleCloseDropdown()
    window.open(post.live?.reportLink, '_blank')?.focus()
  }

  const handleEditPost = () => {
    handleCloseDropdown()

    dispatchDatahubDefaultEvents({
      system: System.HOTMART_CLUB,
      action: Action.CLICK_TO_EDIT_POST,
      entity: Entity.COMMUNITY,
      event: {
        communityId: Number(community.id),
        communityType: CommunityTypeConsume[community.type],
        hasAccessToTheCommunity: true,
        componentName: ComponentName.FEED_CARD_HEADER_DROPDOWN,
        ...deviceInfo
      }
    })

    setOpenModalEditPost(true)
  }

  const handleCloseModalEditPost = () => {
    setOpenModalEditPost(false)
  }

  const handleDeletePost = () => {
    handleCloseDropdown()
    setOpenModalDeletePost(true)
  }

  const handleCloseRemovePostModal = () => {
    setOpenModalDeletePost(false)
  }

  return (
    <div className="_d-flex _flex-column _justify-content-center _align-items-end _position-relative">
      <ActionButton
        className="_d-flex _align-items-center _justify-content-center _ml-4"
        data-test="open-dropdown"
        onClick={handleOpenDropdown}
      >
        <RiMore2Fill size={20} className="_text-gray-900" />
      </ActionButton>

      <Dropdown
        className="_border _border-gray-200 _overflow-hidden"
        open={openDropdown}
        close={handleCloseDropdown}
      >
        {canBlockComments && (
          <FeedCardHeaderDropdownItem
            label={
              post?.isCommentsEnabled
                ? i18n.t('pin_post.block_comments')
                : i18n.t('pin_post.unblock_comments')
            }
            icon={post?.isCommentsEnabled ? RiLock2Line : RiLockUnlockFill}
            borderBottom
            onClick={handleBlockComments}
          />
        )}

        {canBanUser && (
          <FeedCardHeaderDropdownItem
            label={i18n.t('buttons.ban_member')}
            icon={RiUserForbidLine}
            borderBottom
            onClick={handleBanUser}
          />
        )}

        {canPublishScheduledPost && (
          <FeedCardHeaderDropdownItem
            label={i18n.t('buttons.schedule_now')}
            icon={RiCalendarScheduleLine}
            borderBottom
            onClick={handleScheduledPostPublish}
          />
        )}

        {canViewLiveReport && (
          <FeedCardHeaderDropdownItem
            label={i18n.t('live.report.button')}
            icon={RiLiveLine}
            borderBottom
            onClick={handleOpenLiveReport}
          />
        )}

        {canEditPost && (
          <FeedCardHeaderDropdownItem
            label={i18n.t('edit_post.button')}
            icon={RiPencilLine}
            borderBottom
            onClick={handleEditPost}
          />
        )}

        {canDeletePost && (
          <FeedCardHeaderDropdownItem
            label={i18n.t('menu_post_adm.delete')}
            icon={RiDeleteBinLine}
            onClick={handleDeletePost}
          />
        )}
      </Dropdown>

      <BlockCommentsModal
        isOpen={openBlockCommentsModal}
        post={post}
        isCommentsEnabled={post?.isCommentsEnabled}
        closeModal={handleCloseModalBlockComments}
      />

      <BanMemberModal
        isOpen={openModalBanMember}
        userId={post.authorId}
        collectionId={post.ownerCollectionId as number}
        userName={post.authorName}
        closeModal={handleCloseModalBanMember}
      />

      <EditPost
        isOpen={openModalEditPost}
        post={post}
        isScheduledPosts={!!isScheduledPost}
        closeModal={handleCloseModalEditPost}
      />

      <RemovePost
        isOpen={openModalDeletePost}
        idToRemove={post.id}
        isPinnedPost={post.isFixed}
        isScheduledPosts={isScheduledPost}
        closeModal={handleCloseRemovePostModal}
      />
    </div>
  )
}

export default FeedCardHeaderDropdown
